import { type IBundleState } from '@commerce-product-webcomponents/ui';
import { Theme } from '@nextgen-web-framework/all';
import { HttpAugments, request, RequestOptions } from '@nextgen-web-framework/core';
import {
  ListBundleSlot,
  ListEntry,
  ListResponse,
  ListType,
  ListResponseType,
  myListApi,
  PayLoadResponse,
  UpdateListRequest,
  UserListResponse,
} from '../services';
import { categoryDataType } from '../transformers';
import { ApiProjections, InvalidProductException, ListAPIError } from '../types';
import { Exceptions } from '../services/list/models/Exceptions';

export const isItemisInLists = (listData: UserListResponse, productId: string) => {
  let isItemExistInList = false;

  const numberOfLists = listData?.lists?.length as number;
  for (let i = 0; i < numberOfLists; i++) {
    const toBeSearchedList = listData.lists?.[i];
    const itemExisted = toBeSearchedList?.payLoad?.entries?.find(({ product }) => product.id === productId);
    if (itemExisted) {
      isItemExistInList = true;
      break;
    }
  }

  return isItemExistInList;
};

export const isProductInWishlist = (myList: ListResponse, productId: string) =>
  !!myList.payLoad?.entries?.find((p) => p.product.id === productId);

export const isListOnMyShop = (myList: ListResponse) => myList?.isDisplayedOnShop ?? false;

export const getWishlist = (res: UserListResponse) => res?.lists?.find((list) => list.listType === ListType.Wishlist);

export const getListCategory = (myListsData: UserListResponse, category: string) =>
  myListsData.lists?.filter((list) => list.category === category) as ListResponse[];

export const checkShareableListType = (listDetails: PayLoadResponse) => {
  if (listDetails.listType === ListType.Shareable) {
    return true;
  }
  return false;
};

export const getCategoryColor = (
  cmsCategoryData: Array<categoryDataType>,
  category: string,
  isTypeCart: boolean,
  showBackgroundColor?: boolean,
) => {
  const categoryName = getCategoryNameByCategoryId(category as string, cmsCategoryData);
  if (!isTypeCart) {
    const categoryIndex = cmsCategoryData.findIndex((cat) => cat.category_name === categoryName);
    if (categoryIndex !== -1) {
      const { background_color, text_color } = cmsCategoryData?.[categoryIndex] ?? {};
      return showBackgroundColor ? background_color : text_color;
    }

    const [{ background_color, text_color }] =
      cmsCategoryData && cmsCategoryData[0]
        ? cmsCategoryData
        : [
            {
              background_color: '',
              text_color: '',
            },
          ];
    return showBackgroundColor ? background_color : text_color;
  }

  return undefined;
};

export const getButtonThemeColor = (categoryData: categoryDataType[], category: string) => {
  const theme = getCategoryColor(categoryData, category, false, false);
  switch (theme) {
    case '#546223':
      return Theme.GREEN;
    case '#396E75':
      return Theme.BLUE;
    case '#A65523':
      return Theme.ORANGE;
    case '#7F3E3E':
      return Theme.RED;
    default:
      return Theme.PURPLE;
  }
};

export const capitalizeFirstLetter = (listName: string) =>
  listName
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export type IBundleStateWithEntry = IBundleState & {
  entryId: number;
};

export const getBundleState = (bundleSlots: ListBundleSlot.BundleSlot[]) => {
  let budleStateStored: IBundleStateWithEntry[] = [];

  bundleSlots?.forEach((bundleSlot, i) => {
    const derivedBundleState = bundleSlot?.entries?.map((slot) => ({
      slotId: i,
      entryId: slot.entryId,
      productId: slot.product.id,
      totalQty: bundleSlot.maxQty,
      quantity: slot.quantity,
      slotType: bundleSlot.type,
    })) as IBundleStateWithEntry[];
    budleStateStored = [...budleStateStored, ...derivedBundleState];
  });

  return budleStateStored;
};

export const getListBundleSlot = (storedBundleState: IBundleStateWithEntry[], currentBundleState: IBundleState[]) => {
  const listBundleSlots: ListBundleSlot.BundleSlot[] = [];
  const slotIds: number[] = [];

  const updatedBundleStateWithEntry = currentBundleState.map((state) => {
    const slot = storedBundleState.find(({ productId }) => state.productId === productId);
    return {
      ...state,
      entryId: slot?.entryId,
    } as IBundleStateWithEntry;
  });

  updatedBundleStateWithEntry.forEach((state) => {
    if (!slotIds.includes(state.slotId)) {
      slotIds.push(state.slotId);
      listBundleSlots.push({
        id: state.slotId.toString(),
        entries: [],
      });
    }
    const currentSlot = listBundleSlots.find(({ id }) => state.slotId.toString() === id);

    currentSlot?.entries?.push({
      quantity: state.quantity,
      product: {
        id: state.productId,
      },
      entryId: state.entryId,
    });
  });

  return listBundleSlots;
};

export const getExceptionProductsinList = (entries: ListEntry[]) => {
  const invalidProducts: ListEntry[] = [];

  entries?.forEach((entry) => {
    if (entry.errors) {
      const hasInvalidProduct = entry.errors.some((error) => error.title === InvalidProductException.INVALID_PRODUCT);

      if (hasInvalidProduct) {
        invalidProducts.push(entry);
      }
    }
  });

  return {
    invalidProducts,
  };
};

export const getBundleRequestSlots = (bundleState: IBundleState[]) => {
  const nonEmptyBundleState = bundleState.filter(({ quantity }) => quantity);
  const processedSlots: Record<number, ListBundleSlot.BundleSlot> = {};
  nonEmptyBundleState.forEach((data) => {
    const entry = {
      product: {
        id: data.productId,
      },
      quantity: data.quantity,
    };
    if (!processedSlots[data.slotId]) {
      processedSlots[data.slotId] = {
        id: data.slotId.toString(),
        entries: [entry],
      };
    } else {
      processedSlots[data.slotId]?.entries?.push(entry);
    }
  });

  return Object.keys(processedSlots).reduce((acc, num) => {
    acc.push(processedSlots[+num]);
    return acc;
  }, [] as ListBundleSlot.BundleSlot[]);
};

export const productEntryData = (updatedList: UserListResponse, productId: string) => {
  const productEntry = updatedList?.lists?.[0]?.payLoad?.entries?.find((entry) => entry.product.id === productId);
  return productEntry;
};

export const getCategoryNameByCategoryId = (categoryId?: string, categoryData?: categoryDataType[]) => {
  const filteredCategory = categoryData?.find((cat) => cat?.category_id === categoryId);

  return filteredCategory?.category_name || '';
};

export const getCategoryIdByCategoryName = (categoryName?: string, categoryData?: categoryDataType[]) => {
  const filteredCategory = categoryData?.find((cat) => cat?.category_name === categoryName);

  return filteredCategory?.category_id || '';
};

export const listRequestWithVersionFix = (
  requestOptions: RequestOptions,
  augments?: HttpAugments,
  additionInfo?: {
    listId: string;
  },
): Promise<ListResponseType> =>
  request<ListResponseType>(requestOptions, augments).catch(async (e: ListAPIError) => {
    if (e.responseBody?.title === Exceptions.EntityVersionConflict) {
      const isUserAnonymous = SessionService.getSession().isAnonymous();
      if (isUserAnonymous) {
        return request<ListResponseType>(requestOptions, augments);
      }
      if (additionInfo?.listId) {
        const listId = additionInfo?.listId;
        const res = await myListApi.getListById({
          listId,
          projections: ApiProjections.MIN,
        });

        (
          requestOptions.body as unknown as {
            version: number;
          }
        ).version = Number(res.version);
        return request<ListResponseType>(requestOptions, augments);
      }

      if (Array.isArray(requestOptions.body) && requestOptions.body.length === 1) {
        const { listId } = (
          requestOptions.body as unknown as {
            listId: string;
          }[]
        )[0];
        const res = await myListApi.getListById({
          listId,
          projections: ApiProjections.MIN,
        });

        (
          requestOptions.body as unknown as {
            version: number;
          }[]
        )[0].version = Number(res.version);
        return request<ListResponseType>(requestOptions, augments);
      }

      const map: {
        [key: string]: number | undefined;
      } = {};
      const allLists = (requestOptions.body as unknown as UpdateListRequest[]).map(({ listId }) => listId);
      const res = await myListApi.getAllListByUser({
        projections: ApiProjections.FULL,
      });

      for (const { listId, version } of res.lists as ListResponse[]) {
        if (allLists.includes(listId as string)) {
          map[listId as string] = version;
        }
      }

      (requestOptions.body as unknown as UpdateListRequest[]) = (
        requestOptions.body as unknown as UpdateListRequest[]
      ).map((res) => ({
        ...res,
        version: Number(map[res.listId]),
      }));

      return request<ListResponseType>(requestOptions, augments);
    }

    throw e;
  });
